import {
  createContext,
  useContext,
  useCallback,
  useState,
  ReactNode
} from 'react';

import { MainSupplier } from '~types/main';
import { safeJsonParse } from '@/src/utils/storage';

type SupplierContextData = {
  supplier: MainSupplier;
  setSupplier: (supplier: MainSupplier) => void;
  updateSupplier: (supplier: Partial<MainSupplier>) => void;
}

const SupplierContext = createContext<SupplierContextData>({} as SupplierContextData);

type ProviderProps = {
  children: ReactNode;
}

export function SupplierProvider({ children }: ProviderProps) {
  const [currentSupplier, setCurrentSupplier] = useState<MainSupplier>(() => {
    const supplier = localStorage.getItem('@auge:supplier');
    return safeJsonParse<MainSupplier>(supplier, {} as MainSupplier);
  });

  const updateSupplier = useCallback((supplier: Partial<MainSupplier>) => {
    setCurrentSupplier(prev => {
      const updatedSupplier = { ...prev, ...supplier };
      localStorage.setItem('@auge:supplier', JSON.stringify(updatedSupplier));
      return updatedSupplier;
    });
  }, []);

  const handleSetSupplier = useCallback((updatedSupplier: MainSupplier) => {
    localStorage.setItem('@auge:supplier', JSON.stringify(updatedSupplier));
    setCurrentSupplier(updatedSupplier);
  }, []);

  return (
    <SupplierContext.Provider
      value={{
        supplier: currentSupplier,
        setSupplier: handleSetSupplier,
        updateSupplier,
      }}
    >
      {children}
    </SupplierContext.Provider>
  );
}

export function useSupplier() {
  const context = useContext(SupplierContext);

  if (!context) {
    throw new Error("useSupplier must be used within a SupplierProvider.");
  }

  return context;
}
